import { z } from 'zod';

// Conversation quality rubric (salesperson quality — NOT buyer intent, NOT response speed).

export const RUBRIC_VERSION = 'quality-rubric-v1';

export const RUBRIC_DIMENSIONS = [
  {
    key: 'understands_needs',
    weight: 25,
    label: 'Comprende las necesidades del lead',
    description: 'Hace preguntas pertinentes y adapta la conversación a lo que el lead busca.',
  },
  {
    key: 'clear_answers',
    weight: 25,
    label: 'Responde con claridad y pertinencia',
    description: 'Da respuestas concretas y relevantes a lo que se preguntó.',
  },
  {
    key: 'objection_handling',
    weight: 20,
    label: 'Atiende dudas y objeciones',
    description: 'Resuelve dudas u objeciones planteadas. N/A si no hubo objeciones.',
  },
  {
    key: 'next_step',
    weight: 20,
    label: 'Establece un siguiente paso adecuado',
    description: 'Propone o acuerda una acción concreta (cita, envío de información, llamada).',
  },
  {
    key: 'professional_empathy',
    weight: 10,
    label: 'Comunicación profesional y empática',
    description: 'Tono respetuoso, cordial y profesional.',
  },
] as const;

export type RubricDimensionKey = (typeof RUBRIC_DIMENSIONS)[number]['key'];
export const RUBRIC_DIMENSION_KEYS = RUBRIC_DIMENSIONS.map((d) => d.key) as [
  RubricDimensionKey,
  ...RubricDimensionKey[],
];
export const RubricDimensionKeySchema = z.enum(RUBRIC_DIMENSION_KEYS);

export const RATING_ANCHORS: Record<number, string> = {
  0: 'No lo hace pese a una oportunidad clara',
  1: 'Débil',
  2: 'Adecuado con huecos',
  3: 'Bueno',
  4: 'Excelente y específico',
};

export const DimensionEvaluationSchema = z.object({
  key: RubricDimensionKeySchema,
  applicable: z.boolean(),
  /** 0–4; null when not applicable. */
  rating: z.number().int().min(0).max(4).nullable(),
  rationale: z.string(),
  evidenceIds: z.array(z.string()),
});
export type DimensionEvaluation = z.infer<typeof DimensionEvaluationSchema>;

/**
 * Overall = round(100 × Σ(weight × rating / 4) / Σ(applicable weights)).
 * Returns null when no dimension is applicable (never a fabricated 0).
 */
export function computeQualityScore(dimensions: readonly DimensionEvaluation[]): number | null {
  let earned = 0;
  let possible = 0;
  for (const def of RUBRIC_DIMENSIONS) {
    const d = dimensions.find((x) => x.key === def.key);
    if (!d || !d.applicable || d.rating === null) continue;
    earned += (def.weight * d.rating) / 4;
    possible += def.weight;
  }
  if (possible === 0) return null;
  return Math.round((100 * earned) / possible);
}
